import React, {
    useState,
    useEffect,
    useContext,
    createContext,
    FC
} from "react";
import createAuth0Client from "@auth0/auth0-spa-js";
import Auth0Client from "@auth0/auth0-spa-js/dist/typings/Auth0Client";
import config from "./config.json";

interface Context {
    isAuthenticated: boolean;
    user: {
        name: string;
        nickname: string;
        email: string;
    };
    loading: boolean;
    popupOpen: boolean;
    loginWithPopup: (params?: PopupLoginOptions) => Promise<void>;
    handleRedirectCallback: Props["onRedirectCallback"];
    getIdTokenClaims: Auth0Client["getIdTokenClaims"];
    loginWithRedirect: Auth0Client["loginWithRedirect"];
    getTokenSilently: Auth0Client["getTokenSilently"];
    getTokenWithPopup: Auth0Client["getTokenWithPopup"];
    logout: Auth0Client["logout"];
}

export const Auth0Context = createContext<Context>(
    (null as unknown) as Context
);

export const useAuth0 = () => useContext(Auth0Context);

type Props = {
    onRedirectCallback?: (appState: any) => void;
    domain?: string;
    clientId?: string;
    redirectUri?: string;
    audience?: string;
};

const defaultOnRedirectCallback: (props: any) => void = appState => {
    window.history.replaceState(
      {},
      document.title,
      appState && appState.targetUrl
        ? appState.targetUrl
        : window.location.pathname
    );
  }; 

export const Auth0Provider: FC<Props> = ({
    children,
    onRedirectCallback = defaultOnRedirectCallback,
    domain = config.domain,
    clientId = config.clientId,
    redirectUri = window.location.origin,
    audience = config.audience
}) => {
    const [isAuthenticated, setIsAuthenticated] = useState();
    const [user, setUser] = useState();
    const [auth0Client, setAuth0] = useState<Auth0Client>();
    const [loading, setLoading] = useState(true);
    const [popupOpen, setPopupOpen] = useState(false);

    useEffect(() => {
        const initAuth0 = async () => {
            const auth0FromHook = await createAuth0Client({
                domain,
                audience,
                client_id: clientId,
                redirect_uri: redirectUri
            });
            setAuth0(auth0FromHook);

            if (window.location.search.includes("code=")) {
                const {
                    appState
                } = await auth0FromHook.handleRedirectCallback();
                onRedirectCallback(appState);
            }

            const isAuthenticated = await auth0FromHook.isAuthenticated();

            setIsAuthenticated(isAuthenticated);

            if (isAuthenticated) {
                const user = await auth0FromHook.getUser();
                setUser(user);
            }

            setLoading(false);
        };
        initAuth0();
    }, [clientId, domain, redirectUri, onRedirectCallback]);

    const loginWithPopup = async (params = {}) => {
        if (auth0Client === undefined) {
            throw new Error("Auth0Client is not initiated");
        }
        setPopupOpen(true);
        try {
            await auth0Client.loginWithPopup(params);
        } catch (error) {
            console.error(error);
        } finally {
            setPopupOpen(false);
        }
        const user = await auth0Client.getUser();
        setUser(user);
        setIsAuthenticated(true);
    };

    const handleRedirectCallback = async () => {
        if (auth0Client === undefined) {
            throw new Error("Auth0Client is not initiated");
        }
        setLoading(true);
        await auth0Client.handleRedirectCallback();
        const user = await auth0Client.getUser();
        setLoading(false);
        setIsAuthenticated(true);
        setUser(user);
    };
    return (
        <Auth0Context.Provider
            value={{
                isAuthenticated,
                user,
                loading,
                popupOpen,
                loginWithPopup,
                handleRedirectCallback,
                getIdTokenClaims: (...p) =>
                    (auth0Client as Auth0Client).getIdTokenClaims(...p),
                loginWithRedirect: (...p) =>
                    (auth0Client as Auth0Client).loginWithRedirect(...p),
                getTokenSilently: (...p) =>
                    (auth0Client as Auth0Client).getTokenSilently(...p),
                getTokenWithPopup: (...p) =>
                    (auth0Client as Auth0Client).getTokenWithPopup(...p),
                logout: (...p) => (auth0Client as Auth0Client).logout(...p)
            }}
        >
            {children}
        </Auth0Context.Provider>
    );
};
